import * as React from 'react'
import * as ReactDOM from 'react-dom'

import { type Colors, InfoDot } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

interface TooltipSerie {
  name: string
  value: number
  color: Colors
}

interface TooltipData {
  title: string
  mode: 'grouped' | 'stacked'
  value?: number
  series?: TooltipSerie[]
  total?: number
  barColor?: string
}

interface BarChartTooltipProps {
  data: TooltipData
}

// Base styles for tooltip components
const TOOLTIP_STYLES = {
  container: {
    position: 'fixed' as const,
    background: 'white',
    color: '#333',
    padding: '9px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid rgba(0,0,0,0.08)',
    minWidth: '160px',
    lineHeight: '1.3',
    maxWidth: '360px',
    zIndex: 10000,
    pointerEvents: 'none',
    transform: 'translate(-50%, -100%)',
    marginTop: '-12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '6px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#333',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px',
    fontSize: '12px',
    padding: '1px 0',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  dot: {
    marginRight: '6px',
    display: 'flex',
    alignItems: 'center',
  },
  value: {
    fontWeight: '600',
    color: '#333',
    fontSize: '12px',
  },
  divider: {
    borderTop: '1px solid #e0e0e0',
    marginTop: '6px',
    paddingTop: '6px',
  },
  totalLabel: {
    color: '#666',
    fontWeight: '700',
  },
  compactRowWithDot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    padding: '1px 0',
  },
  compactRow: {
    fontSize: '12px',
    textAlign: 'center' as const,
    padding: '1px 0',
  },
} as const

// Component for stacked chart tooltips with series breakdown
const StackedContent: React.FC<{
  series: TooltipSerie[]
  total: number
  title: string
}> = ({ series, total, title }) => (
  <>
    <div style={TOOLTIP_STYLES.titleRow}>
      <span>{title}</span>
    </div>

    {series.map((serie, index) => (
      <div key={index} style={TOOLTIP_STYLES.row}>
        <div style={TOOLTIP_STYLES.label}>
          <div style={TOOLTIP_STYLES.dot}>
            <InfoDot size="small" color={serie.color} />
          </div>
          <span
            style={{
              marginRight: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
            }}
            title={serie.name}
          >
            {serie.name}:
          </span>
        </div>
        <span style={TOOLTIP_STYLES.value}>{serie.value.toLocaleString()}</span>
      </div>
    ))}

    {total > 0 && (
      <div style={TOOLTIP_STYLES.divider}>
        <div style={TOOLTIP_STYLES.row}>
          <span style={TOOLTIP_STYLES.totalLabel}>{i18n.t('common.total')}:</span>
          <span style={{ ...TOOLTIP_STYLES.value, marginLeft: 6 }}>{total.toLocaleString()}</span>
        </div>
      </div>
    )}
  </>
)

// Component for grouped chart tooltips
const GroupedContent: React.FC<{
  value: number
  title: string
  barColor?: string
}> = ({ value, title, barColor }) => (
  <>
    {barColor ? (
      <div style={TOOLTIP_STYLES.compactRowWithDot}>
        <div style={TOOLTIP_STYLES.dot}>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: barColor,
              flexShrink: 0,
            }}
          />
        </div>
        <span
          style={{
            ...TOOLTIP_STYLES.totalLabel,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
          }}
          title={title}
        >
          {title}:
        </span>
        <span style={{ ...TOOLTIP_STYLES.value, marginLeft: 6 }}>{value.toLocaleString()}</span>
      </div>
    ) : (
      <div style={TOOLTIP_STYLES.compactRow}>
        <span
          style={{
            ...TOOLTIP_STYLES.totalLabel,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
          }}
          title={title}
        >
          {title}:
        </span>
        <span style={{ ...TOOLTIP_STYLES.value, marginLeft: 6 }}>{value.toLocaleString()}</span>
      </div>
    )}
  </>
)

const BarChartTooltip: React.FC<BarChartTooltipProps> = ({ data }) => {
  const { title, mode, value, series = [], total = 0, barColor } = data
  const isStacked = mode === 'stacked' && series.length > 0

  const [position, setPosition] = React.useState({ x: 0, y: 0 })

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY })
    }

    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const content = isStacked ? (
    <StackedContent series={series} total={total} title={title} />
  ) : value !== undefined ? (
    <GroupedContent value={value} title={title} barColor={barColor} />
  ) : null

  if (!content) {
    return null
  }

  return ReactDOM.createPortal(
    <div
      style={{
        ...TOOLTIP_STYLES.container,
        left: position.x,
        top: position.y,
      }}
    >
      {content}
    </div>,
    document.body
  )
}

export const SmartBarChartTooltip: React.FC<BarChartTooltipProps> = ({ data }) => {
  return <BarChartTooltip data={data} />
}

export default BarChartTooltip
export type { TooltipData, TooltipSerie }
